import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Email as EmailIcon,
  AutoAwesome as AIIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { confirmEmail } from '../services/auth';
import { colors } from '../theme/expertiseTheme';

type ConfirmationState = 'loading' | 'success' | 'error' | 'manual';

const ConfirmEmailPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [state, setState] = useState<ConfirmationState>('loading');
  const [error, setError] = useState('');
  const [manualUserId, setManualUserId] = useState('');
  const [manualToken, setManualToken] = useState('');
  const [loading, setLoading] = useState(false);

  // Pegar userId e token da query string
  const userId = searchParams.get('userId') || searchParams.get('user_id') || '';
  const rawToken = searchParams.get('token') || '';
  
  // O token pode vir URL-encoded, mas o searchParams já decodifica uma vez
  // Se ainda houver caracteres encoded (ex: %2B, %2F), decodificar novamente
  const token = rawToken.includes('%') ? decodeURIComponent(rawToken) : rawToken;

  // Ref para evitar requisições duplicadas (React 18 StrictMode executa useEffect duas vezes)
  const hasConfirmedRef = useRef(false);

  useEffect(() => {
    // Evitar requisição duplicada
    if (hasConfirmedRef.current) {
      return;
    }
    
    const confirm = async () => {
      if (!userId || !token) {
        // Se não tiver userId e token, mostrar formulário manual
        setState('manual');
        return;
      }

      // Marcar que já iniciou a confirmação
      hasConfirmedRef.current = true;

      try {
        await confirmEmail({ userId, token });
        setState('success');
      } catch (err: any) {
        console.error('Erro na confirmação:', err);
        setError(err.message || 'Erro ao confirmar email');
        setState('error');
      }
    };

    confirm();
  }, [userId, token]);

  const handleManualConfirm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await confirmEmail({ userId: manualUserId, token: manualToken });
      setState('success');
    } catch (err: any) {
      setError(err.message || 'Erro ao confirmar email');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: <AIIcon sx={{ fontSize: 20, color: colors.accent }} />,
      title: 'Ferramentas IA Avançadas',
      description: 'Acesse todas as ferramentas de IA em um único lugar',
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 20, color: colors.accent }} />,
      title: 'Análise em Tempo Real',
      description: 'Monitore e analise resultados instantaneamente',
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 20, color: colors.accent }} />,
      title: 'Segurança Garantida',
      description: 'Seus dados protegidos com criptografia avançada',
    },
  ];

  const renderContent = () => {
    switch (state) {
      case 'loading':
        return (
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress 
              sx={{ color: colors.primary, mb: 3 }} 
              size={60} 
            />
            <Typography variant="h5" sx={{ fontWeight: 600, color: colors.dark, mb: 2 }}>
              Confirmando seu email...
            </Typography>
            <Typography sx={{ color: colors.text.secondary }}>
              Por favor, aguarde enquanto validamos seu email.
            </Typography>
          </Box>
        );

      case 'success':
        return (
          <Box sx={{ textAlign: 'center' }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: 'rgba(0, 99, 0, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 48, color: colors.primary }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 600, color: colors.dark, mb: 2 }}>
              Email confirmado com sucesso!
            </Typography>
            <Typography sx={{ color: colors.text.secondary, mb: 4 }}>
              Sua conta foi verificada. Agora você pode fazer login e acessar todas as funcionalidades do Expertise IA.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/login')}
              sx={{
                py: 1.5,
                px: 6,
                backgroundColor: colors.accent,
                color: colors.dark,
                fontWeight: 600,
                fontSize: '1rem',
                '&:hover': {
                  backgroundColor: '#c9e835',
                },
              }}
            >
              Fazer Login
            </Button>
          </Box>
        );

      case 'error':
        return (
          <Box sx={{ textAlign: 'center' }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: 'rgba(220, 38, 38, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <ErrorIcon sx={{ fontSize: 48, color: colors.error }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 600, color: colors.dark, mb: 2 }}>
              Falha na confirmação
            </Typography>
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              {error}
            </Alert>
            <Typography sx={{ color: colors.text.secondary, mb: 4 }}>
              O link pode ter expirado ou já foi utilizado. Você pode tentar novamente com um novo token ou solicitar um novo email de confirmação.
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                onClick={() => setState('manual')}
                sx={{
                  py: 1.25,
                  px: 4,
                  borderColor: colors.border,
                  color: colors.text.primary,
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: colors.primary,
                    backgroundColor: 'rgba(0, 99, 0, 0.05)',
                  },
                }}
              >
                Inserir Token Manualmente
              </Button>
              <Button
                variant="contained"
                component={RouterLink}
                to="/login"
                sx={{
                  py: 1.25,
                  px: 4,
                  backgroundColor: colors.accent,
                  color: colors.dark,
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#c9e835',
                  },
                }}
              >
                Ir para Login
              </Button>
            </Box>
          </Box>
        );

      case 'manual':
        return (
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, color: colors.dark, mb: 2 }}>
              Confirmar Email Manualmente
            </Typography>
            <Typography sx={{ color: colors.text.secondary, mb: 4 }}>
              Insira o ID do usuário e o token de confirmação que você recebeu por email.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleManualConfirm} noValidate>
              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: colors.text.primary,
                    mb: 1,
                  }}
                >
                  ID do Usuário
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Ex: abc123-def456-..."
                  value={manualUserId}
                  onChange={(e) => setManualUserId(e.target.value)}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: colors.text.light, fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: colors.white,
                    },
                  }}
                />
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: colors.text.primary,
                    mb: 1,
                  }}
                >
                  Token de Confirmação
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Cole o token recebido por email"
                  value={manualToken}
                  onChange={(e) => setManualToken(e.target.value)}
                  required
                  multiline
                  rows={3}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: colors.white,
                    },
                  }}
                />
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || !manualUserId || !manualToken}
                sx={{
                  py: 1.5,
                  backgroundColor: colors.accent,
                  color: colors.dark,
                  fontWeight: 600,
                  fontSize: '1rem',
                  '&:hover': {
                    backgroundColor: '#c9e835',
                  },
                  '&:disabled': {
                    backgroundColor: colors.border,
                    color: colors.text.light,
                  },
                }}
              >
                {loading ? 'Confirmando...' : 'Confirmar Email'}
              </Button>

              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                  component={RouterLink}
                  to="/login"
                  sx={{
                    color: colors.primary,
                    fontWeight: 500,
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  Voltar para Login
                </Button>
              </Box>
            </Box>
          </Box>
        );
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
      }}
    >
      {/* Left Side - Branding */}
      <Box
        sx={{
          width: '45%',
          backgroundColor: colors.dark,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'center',
          px: 8,
          py: 6,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.05,
            background: `
              radial-gradient(circle at 20% 80%, ${colors.accent} 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, ${colors.primary} 0%, transparent 50%)
            `,
          }}
        />

        {/* Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 6, zIndex: 1 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '14px',
              backgroundColor: colors.accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.5rem',
                color: colors.dark,
                fontFamily: 'Outfit, sans-serif',
              }}
            >
              E
            </Typography>
          </Box>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '1.5rem',
              color: colors.white,
              fontFamily: 'Outfit, sans-serif',
            }}
          >
            Expertise IA
          </Typography>
        </Box>

        {/* Description */}
        <Typography
          sx={{
            color: colors.text.onDarkSecondary,
            fontSize: '1rem',
            mb: 5,
            maxWidth: 400,
            zIndex: 1,
          }}
        >
          Plataforma completa de ferramentas de inteligência artificial
        </Typography>

        {/* Features */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, zIndex: 1 }}>
          {features.map((feature, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '10px',
                  backgroundColor: 'rgba(219, 250, 59, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {feature.icon}
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: colors.white,
                    fontSize: '0.9375rem',
                    mb: 0.5,
                  }}
                >
                  {feature.title}
                </Typography>
                <Typography
                  sx={{
                    color: colors.text.onDarkSecondary,
                    fontSize: '0.8125rem',
                    lineHeight: 1.5,
                  }}
                >
                  {feature.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Right Side - Confirmation Content */}
      <Box
        sx={{
          flex: 1,
          backgroundColor: colors.light,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 3, sm: 6 },
          py: 4,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 480 }}>
          {renderContent()}
        </Box>
      </Box>
    </Box>
  );
};

export default ConfirmEmailPage;

